// Shared blog content — used by Blog index and BlogPost detail
export const posts = [
  {
    slug: 'designing-for-9-14',
    category: 'Product Notes',
    title: 'Designing for 9–14: why this age group needs its own platform.',
    excerpt: 'Children aged 9–14 are not "small adults" and not "big kids." They sit in a developmental gap that mainstream social platforms have never been built around. Here\'s how ROMIND KIDS approaches that gap.',
    readTime: '6 min read',
    accent: 'from-violet-500 to-purple-700',
    sections: [
      {
        heading: 'A developmental gap, not an age range',
        body: [
          'The years between 9 and 14 are the years where digital identity, friendship, creativity, and self-expression actively form. It is also the age range where most mainstream social platforms either do not formally allow accounts, or quietly accept them without ever being designed for that audience.',
          'The result is a generation that grows up borrowing adult social products. Children inherit adult feeds, adult metrics, adult comparison culture, and adult monetization patterns — at exactly the age when those patterns can do the most emotional damage.',
        ],
      },
      {
        heading: 'Why "kid-mode" of an adult app is not enough',
        body: [
          'Adding restrictions on top of an adult social platform does not create a platform built for children. The underlying architecture — algorithmic feeds, follower counts, viral mechanics, attention-maximizing notifications — remains optimized for engagement, not for child wellbeing.',
          'ROMIND KIDS starts from a different question. Not "how do we lock down an existing platform?" but "what would a platform look like if it was actually designed around how children aged 9–14 think, feel, and create?"',
        ],
      },
      {
        heading: 'Two sub-groups, one platform',
        body: [
          'Even inside 9–14, there are really two groups. A 9–11 user and a 13–14 user are at very different stages of emotional, creative, and social development. ROMIND KIDS treats them as two related but distinct experiences — gentler structure and simpler prompts for the younger group, deeper creative tools and more nuanced themes for the older group. The safety logic stays constant.',
        ],
      },
      {
        heading: 'What this looks like in product',
        body: [
          'A child-first feed structure (interests, clubs, creative spaces — not one infinite stream). Identity that is expressive but privacy-conscious. Communication built around friends, not open discovery. Creative challenges instead of follower competitions. And a continuous safety layer through RONI, the in-app AI safety companion.',
          'None of these are "kid features." They are platform-level decisions, made because the audience required them.',
        ],
      },
    ],
  },
  {
    slug: 'roni-safety-companion',
    category: 'Insights',
    title: 'RONI is not surveillance — and that distinction matters.',
    excerpt: 'AI safety in a children\'s platform can easily become surveillance theatre. The design intent behind RONI is the opposite: a companion that helps, not a system that watches.',
    readTime: '5 min read',
    accent: 'from-cyan-500 to-blue-700',
    sections: [
      {
        heading: 'The problem with "monitor everything" thinking',
        body: [
          'It is tempting, in a children\'s app, to default to maximum monitoring. Log everything, analyze everything, surface everything to parents. This approach feels safe — but it teaches children that the digital world is a place where they are watched, not protected.',
          'ROMIND KIDS does not aim to build that culture. Children deserve a space that is safer because of how it is designed, not because of how aggressively it is surveilled.',
        ],
      },
      {
        heading: 'RONI as a companion, not a camera',
        body: [
          'RONI is designed as an AI-assisted safety companion that operates inside the platform — looking for unsafe wording, risky interaction patterns, and harmful behaviors. When something looks off, RONI guides the child in the moment: gentle suggestions, in-context support, clear options to report or block.',
          'The framing is intentional. RONI is on the child\'s side, not above them.',
        ],
      },
      {
        heading: 'Where AI ends and human judgment begins',
        body: [
          'AI is good at catching patterns at scale. It is not a substitute for human moderation, parental awareness, or product design that removes the conditions for harm in the first place. RONI is one layer in a stack — alongside reporting flows, blocking, friend-based messaging, age-aware design, and platform-level policies.',
          'No AI guarantees absolute safety. The promise we can make is that safety is a first-order design concern, not a bolt-on feature.',
        ],
      },
    ],
  },
  {
    slug: 'clubs-not-feeds',
    category: 'Product Notes',
    title: 'Why ROMIND KIDS is built around clubs and challenges — not a feed.',
    excerpt: 'The default mental model for social products is "feed first." For children, that default is wrong. Here is why ROMIND KIDS inverts the architecture.',
    readTime: '5 min read',
    accent: 'from-fuchsia-500 to-pink-700',
    sections: [
      {
        heading: 'Feeds optimize for the platform, not the child',
        body: [
          'A traditional social feed is a ranking machine. Its core job is to keep the user scrolling. That objective is misaligned with what is healthy for children in the 9–14 age group, where attention, comparison, and self-image are still forming.',
        ],
      },
      {
        heading: 'Clubs as the social spine',
        body: [
          'Clubs are small, interest-based spaces — Digital Art, Music & Mood, Reading Worlds, Photo & Vision, Creative Projects, Friendship & Identity. They give a child a place that is theirs, defined by what they love, not by what an algorithm thinks will hold them.',
          'Inside a club, the social context is clear, the tone is calibrated, and the moderation environment is shaped for that interest and that age range.',
        ],
      },
      {
        heading: 'Challenges replace the "what should I post?" gap',
        body: [
          'One of the quiet failure modes of generic social platforms is the empty page: a child opens the app, has nothing to say, and ends up scrolling. Challenges fill that gap with gentle creative prompts — visual, written, musical, mindful — built for participation rather than competition.',
          'A challenge is not a leaderboard. It is an invitation to create something small, today.',
        ],
      },
    ],
  },
  {
    slug: 'parents-not-watchers',
    category: 'Insights',
    title: 'Parents are partners, not watchers.',
    excerpt: 'Parental trust in a children\'s platform is built through clarity, not through cameras. A note for parents, partners, and policy reviewers.',
    readTime: '4 min read',
    accent: 'from-emerald-500 to-cyan-700',
    sections: [
      {
        heading: 'What parents actually need',
        body: [
          'When parents evaluate a new app for their child, the underlying question is rarely "can I see every message?" — it is "is this place built with my child\'s wellbeing in mind?" Trust is about how the platform behaves, not about how invasive its parental view is.',
        ],
      },
      {
        heading: 'Two audiences, one product',
        body: [
          'The app design and experience inside ROMIND KIDS — the feed, the clubs, the challenges, the creative tools, the identity layer — is for children. It is built around how they create, communicate, and grow. The safety, privacy, and trust architecture is built for parents.',
          'Same platform. Two coexisting layers. Each calibrated for its audience.',
        ],
      },
      {
        heading: 'A trust contract, not a control panel',
        body: [
          'ROMIND KIDS approaches the parent relationship as a trust contract: clear safety design, transparent product direction, honest communication about what is built today and what is planned next. The goal is not to give parents a surveillance dashboard. The goal is to give parents a platform they can recommend to their child without anxiety.',
        ],
      },
    ],
  },
];
